import { AuthService } from './authService';
import { ItemBinSizeDto } from '../types/itemBinSize';
import {
  ItemBinSizeGroupDto,
  CreateItemBinSizeGroupRequest,
  UpdateItemBinSizeGroupRequest,
  AssignItemsToGroupRequest,
  GroupBinSizeLineRequest,
  ItemBinSizeGroupItemDto
} from '../types/itemBinSizeGroup';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ItemBinSizeGroupService {
  private baseUrl = `${API_BASE_URL}/item-bin-size-groups`;

  async getCurrent(): Promise<ItemBinSizeGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getByCompanyNo(companyNo: string): Promise<ItemBinSizeGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getByGroupId(companyNo: string, groupId: number): Promise<ItemBinSizeGroupDto | null> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${groupId}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) return null;
      throw error;
    }
  }

  async create(request: CreateItemBinSizeGroupRequest): Promise<ItemBinSizeGroupDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  async update(companyNo: string, groupId: number, request: UpdateItemBinSizeGroupRequest): Promise<ItemBinSizeGroupDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${groupId}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  async delete(companyNo: string, groupId: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${groupId}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  // Group items
  async getItems(companyNo: string, groupId: number): Promise<ItemBinSizeGroupItemDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${groupId}/items`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async assignItems(companyNo: string, groupId: number, request: AssignItemsToGroupRequest): Promise<void> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/${companyNo}/${groupId}/items`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  async removeItem(companyNo: string, groupId: number, itemNo: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${groupId}/items/${encodeURIComponent(itemNo)}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  // Group bin size lines
  async getBinSizeLines(companyNo: string, groupId: number): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${groupId}/bin-sizes`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async updateBinSizeLines(companyNo: string, groupId: number, lines: GroupBinSizeLineRequest[]): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${groupId}/bin-sizes`, lines);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }
}

export const itemBinSizeGroupService = new ItemBinSizeGroupService();
